import type { GeneratedAsset } from '@pulsify/shared/dto'
import { AlertTriangle, ImageOff, Loader2 } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useGeneratedAssets } from '~/entities/generated-asset/model/use-generated-assets'
import { aspectLabel } from '~/features/shared/lib/aspect-label'
import * as m from '~/paraglide/messages'
import { extensionFor, SUPPORTED_IMAGE_MIME_TYPES, urlToFile } from '~/shared/lib/asset-image'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/shared/ui/dialog'

interface AssetLibraryPickerProps {
  trigger: ReactNode
  onSelect: (file: File, asset: GeneratedAsset) => void | Promise<void>
}

export function AssetLibraryPicker({ trigger, onSelect }: AssetLibraryPickerProps) {
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const [open, setOpen] = useState(false)
  const [loadingId, setLoadingId] = useState<string | null>(null)
  const { data: assets, isLoading, isError } = useGeneratedAssets(businessId)

  const images = (assets ?? []).filter((asset) =>
    (SUPPORTED_IMAGE_MIME_TYPES as readonly string[]).includes(asset.mimeType),
  )

  async function handlePick(asset: GeneratedAsset) {
    setLoadingId(asset.id)
    try {
      const file = await urlToFile(
        asset.url,
        `${asset.title || asset.tool}.${extensionFor(asset.mimeType)}`,
      )
      await onSelect(file, asset)
      setOpen(false)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : m.assetLibraryPickerLoadError())
    } finally {
      setLoadingId(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{m.assetLibraryPickerTitle()}</DialogTitle>
          <DialogDescription>{m.assetLibraryPickerDescription()}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] min-h-72 overflow-y-auto">
          {isLoading ? (
            <div className="flex min-h-72 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : isError ? (
            <div className="flex min-h-72 flex-col items-center justify-center gap-2 text-center text-muted-foreground">
              <AlertTriangle className="h-8 w-8" />
              <p className="text-sm">{m.assetLibraryPickerListError()}</p>
            </div>
          ) : images.length === 0 ? (
            <div className="flex min-h-72 flex-col items-center justify-center gap-2 text-center text-muted-foreground">
              <ImageOff className="h-8 w-8" />
              <p className="text-sm">{m.assetLibraryPickerEmpty()}</p>
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(9rem,11rem))] content-start justify-center gap-4">
              {images.map((asset) => {
                const format = aspectLabel(asset.width, asset.height)
                return (
                  <button
                    key={asset.id}
                    type="button"
                    aria-label={asset.title || asset.tool}
                    disabled={loadingId !== null}
                    onClick={() => handlePick(asset)}
                    className="group relative flex flex-col overflow-hidden rounded-lg border bg-muted text-left outline-none transition-colors hover:border-primary focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50"
                  >
                    <div className="relative flex aspect-square items-center justify-center p-2">
                      <img
                        src={asset.url}
                        alt={asset.title}
                        loading="lazy"
                        width={asset.width ?? undefined}
                        height={asset.height ?? undefined}
                        className="h-auto max-h-full w-auto max-w-full rounded-sm object-contain shadow-sm"
                      />
                      {loadingId === asset.id && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                          <Loader2 className="h-5 w-5 animate-spin text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 border-t bg-background px-2 py-1.5">
                      <p className="min-w-0 flex-1 truncate text-xs">{asset.title || asset.tool}</p>
                      {format && (
                        <span className="shrink-0 rounded-sm bg-muted px-1.5 py-0.5 font-medium text-xs text-muted-foreground tabular-nums">
                          {format}
                        </span>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
